import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader2, Camera, Plus, X } from 'lucide-react';
import { apiService } from '@/services/api';
import { AssinaturaManager } from '@/components/apreciacao/assinatura-manager';

export function AdminUsuarioForm() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdicao = Boolean(id);

  const [loading, setLoading] = useState(isEdicao);
  const [saving, setSaving] = useState(false);
  const [empresas, setEmpresas] = useState<{ id: string; nome: string }[]>([]);
  const [squads, setSquads] = useState<{ id: string; nome: string; regiao?: string | null }[]>([]);
  const [cargosSugeridos, setCargosSugeridos] = useState<string[]>([]);
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [novaSenha, setNovaSenha] = useState('');
  const [role, setRole] = useState<'admin' | 'user' | 'cliente'>('user');
  const [ativo, setAtivo] = useState(true);
  const [empresaId, setEmpresaId] = useState<string>('');
  const [squadId, setSquadId] = useState<string>('');
  const [crea, setCrea] = useState('');
  const [cargo, setCargo] = useState('');
  const [descricao, setDescricao] = useState('');
  const [fotoUrl, setFotoUrl] = useState<string>('');
  const [assinaturaUrl, setAssinaturaUrl] = useState<string | undefined>(undefined);
  const [assinaturaDigital, setAssinaturaDigital] = useState<string | undefined>(undefined);

  const [showNovaEmpresa, setShowNovaEmpresa] = useState(false);
  const [novaEmpresaNome, setNovaEmpresaNome] = useState('');
  const [novaEmpresaCnpj, setNovaEmpresaCnpj] = useState('');
  const [criandoEmpresa, setCriandoEmpresa] = useState(false);

  const handleCriarEmpresa = async () => {
    if (!novaEmpresaNome.trim()) return;
    setCriandoEmpresa(true);
    try {
      const res = await apiService.criarEmpresa({ nome: novaEmpresaNome.trim(), cnpj: novaEmpresaCnpj.trim() || undefined });
      if (res.success && res.data) {
        const nova = res.data as { id: string; nome: string };
        setEmpresas([...empresas, nova]);
        setEmpresaId(nova.id);
        setShowNovaEmpresa(false);
        setNovaEmpresaNome('');
        setNovaEmpresaCnpj('');
      } else {
        alert(res.message || 'Erro ao criar empresa');
      }
    } catch (err) {
      console.error(err);
      alert('Erro ao criar empresa');
    } finally {
      setCriandoEmpresa(false);
    }
  };

  useEffect(() => {
    const loadEmpresas = async () => {
      const res = await apiService.listarEmpresas({});
      if (res.success && res.data) {
        setEmpresas(Array.isArray(res.data) ? res.data : []);
      }
    };
    const loadSquads = async () => {
      const res = await apiService.listarSquads({ ativo: true });
      if (res.success && res.data) {
        setSquads(Array.isArray(res.data) ? res.data : []);
      }
    };
    const loadCargos = async () => {
      const res = await apiService.listarCargos();
      if (res.success && res.data) {
        setCargosSugeridos(Array.isArray(res.data) ? res.data : []);
      }
    };
    loadEmpresas();
    loadSquads();
    loadCargos();
  }, []);

  useEffect(() => {
    if (!id) return;
    const load = async () => {
      setLoading(true);
      try {
        const res = await apiService.buscarUsuario(id);
        if (res.success && res.data) {
          const u = res.data as any;
          setNome(u.nome ?? '');
          setEmail(u.email ?? '');
          setRole((['admin', 'user', 'cliente'].includes(u.role) ? u.role : 'user') as 'admin' | 'user' | 'cliente');
          setAtivo(u.ativo !== false);
          setEmpresaId(u.empresaId ?? '');
          setSquadId(u.squadId ?? '');
          setCrea(u.crea ?? '');
          setCargo(u.cargo ?? '');
          setDescricao(u.descricao ?? '');
          setFotoUrl(u.fotoUrl ?? '');
          setAssinaturaUrl(u.assinaturaUrl ?? undefined);
          setAssinaturaDigital(u.assinaturaDigital ?? undefined);
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nome.trim() || !email.trim()) return;
    if (!isEdicao && !senha.trim()) return;

    setSaving(true);
    try {
      if (isEdicao) {
        const payload: Parameters<typeof apiService.atualizarUsuario>[1] = {
          nome: nome.trim(),
          email: email.trim(),
          role,
          ativo,
          empresaId: empresaId || undefined,
          squadId: squadId || null,
          crea: crea.trim() || undefined,
          cargo: cargo.trim() || undefined,
          descricao: descricao.trim() || undefined,
          fotoUrl: fotoUrl || null,
          assinaturaUrl: assinaturaUrl || undefined,
          assinaturaDigital: assinaturaDigital || undefined,
        };
        if (novaSenha.trim()) payload.senha = novaSenha;
        const res = await apiService.atualizarUsuario(id!, payload);
        if (res.success) {
          navigate('/admin/usuarios');
          return;
        }
        alert(res.message || 'Erro ao atualizar usuário');
      } else {
        const res = await apiService.criarUsuario({
          nome: nome.trim(),
          email: email.trim(),
          senha: senha,
          role,
          ativo,
          empresaId: empresaId || null,
          squadId: squadId || null,
          crea: crea.trim() || null,
          cargo: cargo.trim() || null,
          descricao: descricao.trim() || null,
        });
        if (res.success) {
          navigate('/admin/usuarios');
          return;
        }
        alert(res.message || 'Erro ao criar usuário');
      }
    } catch (err) {
      console.error(err);
      alert('Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-schmersal-blue" />
      </div>
    );
  }

  const fotoExibir = fotoUrl && (fotoUrl.startsWith('data:') || fotoUrl.startsWith('http') || fotoUrl.startsWith('/')) ? fotoUrl : fotoUrl || null;

  return (
    <div className="max-w-2xl">
      <h1 className="text-2xl font-bold text-schmersal-gray-dark dark:text-white mb-6">
        {isEdicao ? 'Editar usuário' : 'Novo usuário'}
      </h1>
      <form onSubmit={handleSubmit} className="space-y-4 bg-white dark:bg-schmersal-gray-darker rounded-lg shadow p-6">
        {isEdicao && (
          <div>
            <label className="block text-sm font-medium text-schmersal-gray-dark dark:text-white mb-2">Foto</label>
            <div className="flex items-start gap-4">
              <div className="w-20 h-20 rounded-full overflow-hidden bg-schmersal-gray-light dark:bg-schmersal-gray-dark flex items-center justify-center border border-rg-line dark:border-schmersal-gray-dark">
                {fotoExibir ? (
                  <img src={fotoExibir} alt="Foto" className="w-full h-full object-cover" />
                ) : (
                  <Camera className="h-8 w-8 text-schmersal-gray-medium dark:text-schmersal-gray-light" />
                )}
              </div>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    const reader = new FileReader();
                    reader.onloadend = () => setFotoUrl(reader.result as string);
                    reader.readAsDataURL(file);
                  }
                }}
                className="block w-full text-sm text-schmersal-gray-dark dark:text-white file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-schmersal-blue file:text-white file:font-medium"
              />
            </div>
          </div>
        )}
        <div>
          <label className="block text-sm font-medium text-schmersal-gray-dark dark:text-white mb-1">Nome *</label>
          <input
            type="text"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            required
            className="w-full px-3 py-2 border border-schmersal-gray-light dark:border-schmersal-gray-dark rounded-lg bg-white dark:bg-schmersal-gray-dark text-schmersal-gray-dark dark:text-white"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-schmersal-gray-dark dark:text-white mb-1">Email *</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="w-full px-3 py-2 border border-schmersal-gray-light dark:border-schmersal-gray-dark rounded-lg bg-white dark:bg-schmersal-gray-dark text-schmersal-gray-dark dark:text-white"
          />
        </div>
        {!isEdicao ? (
          <div>
            <label className="block text-sm font-medium text-schmersal-gray-dark dark:text-white mb-1">Senha *</label>
            <input
              type="password"
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
              required
              className="w-full px-3 py-2 border border-schmersal-gray-light dark:border-schmersal-gray-dark rounded-lg bg-white dark:bg-schmersal-gray-dark text-schmersal-gray-dark dark:text-white"
            />
          </div>
        ) : (
          <div>
            <label className="block text-sm font-medium text-schmersal-gray-dark dark:text-white mb-1">Nova senha (opcional)</label>
            <input
              type="password"
              value={novaSenha}
              onChange={(e) => setNovaSenha(e.target.value)}
              placeholder="Deixe em branco para manter a atual"
              className="w-full px-3 py-2 border border-schmersal-gray-light dark:border-schmersal-gray-dark rounded-lg bg-white dark:bg-schmersal-gray-dark text-schmersal-gray-dark dark:text-white"
            />
          </div>
        )}
        <div>
          <label className="block text-sm font-medium text-schmersal-gray-dark dark:text-white mb-1">Perfil</label>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as 'admin' | 'user' | 'cliente')}
            className="w-full px-3 py-2 border border-schmersal-gray-light dark:border-schmersal-gray-dark rounded-lg bg-white dark:bg-schmersal-gray-dark text-schmersal-gray-dark dark:text-white"
          >
            <option value="user">Usuário</option>
            <option value="admin">Administrador</option>
            <option value="cliente">Cliente (Portal)</option>
          </select>
          {role === 'cliente' && !empresaId && (
            <p className="text-xs text-amber-600 mt-1">Selecione uma empresa para o usuario cliente</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="ativo"
            checked={ativo}
            onChange={(e) => setAtivo(e.target.checked)}
            className="rounded border-schmersal-gray-light dark:border-schmersal-gray-dark"
          />
          <label htmlFor="ativo" className="text-sm text-schmersal-gray-dark dark:text-white">Ativo</label>
        </div>
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="block text-sm font-medium text-schmersal-gray-dark dark:text-white">Empresa</label>
            {!showNovaEmpresa && (
              <button
                type="button"
                onClick={() => setShowNovaEmpresa(true)}
                className="text-xs flex items-center gap-1 text-schmersal-blue hover:text-schmersal-blue-dark dark:text-schmersal-blue-light dark:hover:text-schmersal-blue"
              >
                <Plus className="h-3 w-3" /> Nova Empresa
              </button>
            )}
          </div>
          {showNovaEmpresa ? (
            <div className="p-3 border border-schmersal-gray-light dark:border-schmersal-gray-dark rounded-lg bg-schmersal-gray-light/30 dark:bg-schmersal-gray-dark/30 mb-2">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-schmersal-gray-dark dark:text-white">Criar Nova Empresa</span>
                <button type="button" onClick={() => setShowNovaEmpresa(false)} className="text-schmersal-gray-medium hover:text-schmersal-gray-dark dark:text-schmersal-gray-light dark:hover:text-white">
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
                <input
                  type="text"
                  value={novaEmpresaNome}
                  onChange={(e) => setNovaEmpresaNome(e.target.value)}
                  placeholder="Nome da empresa *"
                  className="w-full px-2 py-1 text-sm border border-schmersal-gray-light dark:border-schmersal-gray-dark rounded bg-white dark:bg-schmersal-gray-dark text-schmersal-gray-dark dark:text-white"
                />
                <input
                  type="text"
                  value={novaEmpresaCnpj}
                  onChange={(e) => setNovaEmpresaCnpj(e.target.value)}
                  placeholder="CNPJ (opcional)"
                  className="w-full px-2 py-1 text-sm border border-schmersal-gray-light dark:border-schmersal-gray-dark rounded bg-white dark:bg-schmersal-gray-dark text-schmersal-gray-dark dark:text-white"
                />
              </div>
              <button
                type="button"
                onClick={handleCriarEmpresa}
                disabled={criandoEmpresa || !novaEmpresaNome.trim()}
                className="w-full px-3 py-1.5 bg-schmersal-blue text-white text-sm rounded hover:bg-schmersal-blue-dark disabled:opacity-50 flex justify-center items-center gap-2"
              >
                {criandoEmpresa && <Loader2 className="h-3 w-3 animate-spin" />}
                Salvar Empresa
              </button>
            </div>
          ) : null}
          <select
            value={empresaId}
            onChange={(e) => setEmpresaId(e.target.value)}
            className="w-full px-3 py-2 border border-schmersal-gray-light dark:border-schmersal-gray-dark rounded-lg bg-white dark:bg-schmersal-gray-dark text-schmersal-gray-dark dark:text-white"
          >
            <option value="">Nenhuma</option>
            {empresas.map((emp) => (
              <option key={emp.id} value={emp.id}>{emp.nome}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-schmersal-gray-dark dark:text-white mb-1">Squad</label>
          <select
            value={squadId}
            onChange={(e) => setSquadId(e.target.value)}
            className="w-full px-3 py-2 border border-schmersal-gray-light dark:border-schmersal-gray-dark rounded-lg bg-white dark:bg-schmersal-gray-dark text-schmersal-gray-dark dark:text-white"
          >
            <option value="">Nenhum (acesso global — Chefe)</option>
            {squads.map((sq) => (
              <option key={sq.id} value={sq.id}>{sq.nome}{sq.regiao ? ` — ${sq.regiao}` : ''}</option>
            ))}
          </select>
          <p className="text-xs text-schmersal-gray-medium dark:text-schmersal-gray-light mt-1">
            Usuários (não-admin) só enxergam projetos e colegas do próprio squad.
          </p>
        </div>
        <div>
          <label className="block text-sm font-medium text-schmersal-gray-dark dark:text-white mb-1">CREA</label>
          <input
            type="text"
            value={crea}
            onChange={(e) => setCrea(e.target.value)}
            className="w-full px-3 py-2 border border-schmersal-gray-light dark:border-schmersal-gray-dark rounded-lg bg-white dark:bg-schmersal-gray-dark text-schmersal-gray-dark dark:text-white"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-schmersal-gray-dark dark:text-white mb-1">Função / Cargo</label>
          <input
            type="text"
            value={cargo}
            onChange={(e) => setCargo(e.target.value)}
            list="cargos-sugeridos"
            placeholder="Digite ou selecione um cargo"
            className="w-full px-3 py-2 border border-schmersal-gray-light dark:border-schmersal-gray-dark rounded-lg bg-white dark:bg-schmersal-gray-dark text-schmersal-gray-dark dark:text-white"
          />
          <datalist id="cargos-sugeridos">
            {cargosSugeridos.map((c) => (
              <option key={c} value={c} />
            ))}
          </datalist>
          <p className="text-xs text-schmersal-gray-medium dark:text-schmersal-gray-light mt-1">
            Pode digitar um cargo novo ou escolher um já existente na lista.
          </p>
        </div>
        <div>
          <label className="block text-sm font-medium text-schmersal-gray-dark dark:text-white mb-1">Descrição detalhada</label>
          <textarea
            value={descricao}
            onChange={(e) => setDescricao(e.target.value)}
            placeholder="Ex.: Responsável por vistorias&#10;Elaboração de laudos..."
            className="w-full px-3 py-2 border border-schmersal-gray-light dark:border-schmersal-gray-dark rounded-lg bg-white dark:bg-schmersal-gray-dark text-schmersal-gray-dark dark:text-white h-24 whitespace-pre-wrap"
          />
        </div>
        {isEdicao && (
          <div>
            <AssinaturaManager
              assinaturaUrl={assinaturaUrl}
              assinaturaDigital={assinaturaDigital}
              onChange={(url, digital) => {
                setAssinaturaUrl(url);
                setAssinaturaDigital(digital);
              }}
              modoEdicao
              label="Assinatura padrão"
            />
          </div>
        )}
        <div className="flex gap-3 pt-4">
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 bg-schmersal-blue text-white rounded-lg hover:bg-schmersal-blue-dark disabled:opacity-50 flex items-center gap-2"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {isEdicao ? 'Salvar' : 'Criar'}
          </button>
          <button
            type="button"
            onClick={() => navigate('/admin/usuarios')}
            className="px-4 py-2 border border-schmersal-gray-light dark:border-schmersal-gray-dark rounded-lg text-schmersal-gray-dark dark:text-white hover:bg-schmersal-gray-light dark:hover:bg-schmersal-gray-dark"
          >
            Cancelar
          </button>
        </div>
      </form>
    </div>
  );
}
